import { Button, Episode, FontIcon } from '@intigral/shared';
import { Carousel2 } from '@intigral/ui';
import { find } from 'lodash';
import React, { useEffect, useState } from 'react';
import Swiper from 'swiper';
import SelectDropdown from '../../elements/SelectDropdown';
import h from '../../../helpers/common';
import url from '../../../helpers/url';
import { log } from '@intigral/utils';
import { useConfiguration } from '../../../hooks/useConfiguration';
import { useEpisodeActions } from './useEpisodeAction';
import { fetchSeriesHistory } from '../../../hooks/useSeriesNextEpisode';

const swiperSettings = {
    slidesPerView: 'auto',
    spaceBetween: 10,
    virtual: false,
    breakpoints: {
        560: { slidesPerView: 1 },
        768: { slidesPerView: 2 },
        924: { slidesPerView: 3 },
        1212: { slidesPerView: 4 },
        1500: { slidesPerView: 5 },
    },
};

interface Episode {
    Id: string;
    duration: number;
    streamingUrl: string;
    guid: string;
    tvSeasonNumber: number;
    tvSeasonEpisodeNumber: number;
    image_landscape: string;
    text: string;
    description: string;
    source: {
        hls?: string;
        dash?: string;
    };
}

interface SeasonHistory {
    id: string;
    timeCode: string;
}

/**
 * Returns episode progress percentage formatted to be used as CSS width or `false` if progress data is not provided
 */
const mapEpisodesToHistory = (item: Episode, list: SeasonHistory[]): string | boolean => {
    const seasonHistory = find(list, (a) => a.id === item.Id);

    if (seasonHistory) {
        return (parseInt(seasonHistory.timeCode, 10) / item.duration) * 100 + '%';
    } else {
        return false;
    }
};

const EpisodesCarousel = React.forwardRef(
    (
        {
            seriesPlaylist,
            seasonsList,
            selectedSeasonId,
            setSelectedSeasonId,
            closeOverlay,
        }: {
            seriesPlaylist: Episode[];
            seasonsList: { label: Record<string, string>; [key: string]: any }[];
            selectedSeasonId: string;
            setSelectedSeasonId: (value: string) => void;
            closeOverlay: () => void;
        },
        ref: React.RefObject<{ swiper: Swiper }>
    ) => {
        const [swiper, setSwiper] = useState(ref.current?.swiper);
        const { data: config } = useConfiguration();

        const { navigateToEpisode } = useEpisodeActions();
        const { data: seriesHistory } = fetchSeriesHistory(url.getQuery('id'));

        const getEpisodeProgress = (episode: Episode) => {
            return (
                mapEpisodesToHistory(
                    episode,
                    seriesHistory?.seriesHistory?.map((history) => ({ id: history?.id, timeCode: history?.timeCode }))
                ) || '0%'
            );
        };
        const singleEpisodeClickHandler = async (episode: Episode, activeEpisodeIdx: number, index: number) => {
            swiper.slideTo(index);
            if (activeEpisodeIdx > 0) {
                swiper.slides[0].classList.remove('currentEpisode');
            }
            swiper.slides[index].classList.add('currentEpisode');
            await navigateToEpisode(episode);
            closeOverlay();
        };

        useEffect(() => {
            setSwiper(ref.current?.swiper);
        }, [ref.current?.swiper]);

        return (
            <section className="tv-show-section">
                <Button
                    round
                    small
                    onClick={() => {
                        closeOverlay();
                    }}
                    style={{ float: 'inline-end' }}
                >
                    <FontIcon name="close" />
                </Button>
                <SelectDropdown
                    items={seasonsList}
                    onChange={(value) => {
                        if (swiper) {
                            const prev = h.getIndexOfArrayByAttrVal(seriesPlaylist, 'Id', atob(url.getQuery('id')));
                            swiper.slides[prev].classList.remove('currentEpisode');
                        }
                        setSelectedSeasonId(value);
                        const currentSeason = find(seasonsList, (a) => a.Id === value);
                        url.insertParams('season', currentSeason.tvSeasonNumber);
                    }}
                    listTypeClass="Seasons-dropdown"
                    activeClass="active-item"
                    selected={selectedSeasonId ? selectedSeasonId : seasonsList[0].Id}
                />
                <div className="container full-width">
                    <Carousel2 swiperSettings={swiperSettings} ref={ref}>
                        {seriesPlaylist.length > 0 &&
                            seriesPlaylist.map((episode, index) => {
                                const activeEpisodeIdx = h.getIndexOfArrayByAttrVal(
                                    seriesPlaylist,
                                    'Id',
                                    atob(url.getQuery('id'))
                                );
                                const episodeProgress = getEpisodeProgress(episode);
                                return (
                                    <Episode
                                        key={episode.Id ?? episode.guid ?? index}
                                        image={h.getImagizerUrl(
                                            episode.image_landscape,
                                            config?.imagizer?.components_images.SEASON_EPISODES_ITEM
                                        )}
                                        title={episode.text}
                                        description={episode.description}
                                        number={episode.tvSeasonEpisodeNumber.toString()}
                                        link={{}}
                                        onClick={() => singleEpisodeClickHandler(episode, activeEpisodeIdx, index)}
                                        isVertical
                                        progress={typeof episodeProgress === 'string' ? episodeProgress : null}
                                    />
                                );
                            })}
                    </Carousel2>
                </div>
            </section>
        );
    }
);

export default EpisodesCarousel;
